import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { printDate } from '../../../helpers/Utils'
import { useGetAllCustomersQuery, useGetAllUnitsQuery } from '../../../redux/api/customersApi'
import { useGetOrderQuery, useGetOrdersFormQuery } from '../../../redux/api/ordersApi'
import CsvModal from '../../common/Modals/CsvModal/CsvModal'
import { CsvTableRow, CsvTableSection } from '../../common/Modals/CsvModal/Table/CsvTable'
import { downloadFile } from '../../../helpers/FileDownload'
import { OrdersFormFieldViewModel, OrdersFormViewModel, OrderViewModel } from '@ail/orders-api'

interface RequestDetailsModalProps {
  requestId?: string
  onClose: () => void
}

export function RequestDetailsModal(props: RequestDetailsModalProps) {
  const [sections, setSections] = useState<CsvTableSection[]>()

  const { t, i18n } = useTranslation()
  const language = i18n.language

  const order = useGetOrderQuery(props.requestId ?? skipToken)
  const form = useGetOrdersFormQuery(order.data?.formId ?? skipToken)
  const allCustomers = useGetAllCustomersQuery()
  const allUnits = useGetAllUnitsQuery()
  const loading = order.isFetching || form.isFetching || allCustomers.isFetching

  useEffect(() => {
    if (order.data && form.data && allCustomers.data) {
      constructTable(order.data, form.data)
    }
  }, [order.data, form.data, allCustomers.data])

  const downloadDocument = (value?: string) => {
    if (value) {
      const fileArr = value.split('|')
      const fileURL = fileArr[1]
      const filename = fileArr[0]
      if (fileURL && filename) {
        downloadFile({ fileURL, filename })
      }
    }
  }

  const constructTable = (order: OrderViewModel, form: OrdersFormViewModel) => {
    let csvTable: CsvTableSection[] = []

    csvTable = [
      ...csvTable,
      {
        name: t('CUSTOMER') || 'Customer',
        icon: 'icon-customers',
        columnCount: 2,
        rows: {
          0: {
            row: {
              1: { value: t('COMPANY_NAME') || 'Company', copy: false },
              2: { value: order.company?.companyName, copy: true },
            },
          },
          1: {
            row: {
              1: { value: t('COST_CENTER') || 'Cost center', copy: false },
              2: { value: order.costCenter?.costCenterName, copy: true },
            },
          },
          2: {
            row: {
              1: { value: t('USER') || 'User', copy: false },
              2: { value: order.userDisplayName, copy: true },
            },
          },
        },
      },
    ]

    form.sections.map((section, index) => {
      const rows: CsvTableRow[] = []

      if (index === 0) {
        return
      }

      form.formFields
        .filter((field) => field.section === section.name)
        .map((field) => {
          const value = getFieldValue(field, order.data, language)
          const isFile = field.type === 'upload' && value
          const row: CsvTableRow = {
            row: {
              1: { value: field.tr?.[language] || field.label, copy: false },
              2: {
                value,
                copy: !isFile,
                download: isFile ? () => downloadDocument(order.data?.[field.name]) : undefined,
              },
            },
          }
          rows.push(row)
        })

      csvTable.push({
        name: section.tr?.[language],
        icon: section.icon,
        columnCount: 2,
        rows,
      })
    })

    const stepRows: CsvTableRow[] = []

    order.approvalProcess?.steps?.map((step) => {
      const notes = Object.keys(step.notes)
        .map((key) => step.notes[key])
        .join(', ')
      stepRows.push({
        row: {
          1: { value: step.step.toString(), copy: false },
          2: { value: step.userDisplayName, copy: true },
          3: { value: `${printDate(step.timestamp, true)}`, copy: false },
          4: { value: notes, copy: false },
        },
      })
    })

    csvTable.push({
      icon: 'icon-sign',
      name: t('APPROVE/SIGN') || '',
      columnCount: 2,
      rows: [
        {
          row: {
            1: { value: t('ID') || 'Step', copy: false },
            2: { value: order.id, copy: !!order.id },
          },
        },
        {
          row: {
            1: { value: t('STATUS') || 'Step', copy: false },
            2: {
              value: (order.approvalProcess?.status && t(order.approvalProcess?.status?.toUpperCase())) || '',
              copy: true,
            },
          },
        },
      ],
    })

    csvTable.push({
      columnCount: 4,
      rows: [
        {
          row: {
            1: { value: t('STEP') || 'Step', copy: false },
            2: { value: t('USER_NAME') || 'User name', copy: false },
            3: { value: t('DATE') || 'Date', copy: false },
            4: { value: t('NOTE') || 'Note', copy: false },
          },
        },
        ...stepRows,
      ],
    })

    setSections(csvTable)
  }

  const getFieldValue = (
    field: OrdersFormFieldViewModel,
    data: Record<string, string | undefined>,
    lang: string
  ): string => {
    switch (field.type) {
      case 'radio':
        return (
          field.options.find((option) => option.value === data[field.name])?.tr?.[lang] ??
          field.options.find((option) => option.value === data[field.name])?.name ??
          ''
        )
      case 'checkBox':
        return data[field.name] === 'true' ? t('YES') : t('NO')
      case 'customersSelect':
      case 'customersSelectUnlinked': {
        const customer = allCustomers.data?.find((c) => c.customer?.id === data[field.name])
        return customer?.customer?.name ?? ''
      }
      case 'unitsSelect':
      case 'unitsSelectUnlinked': {
        const unit = allUnits.data?.find((u) => u.unit?.id.toLowerCase() === data[field.name]?.toLowerCase())
        return unit?.unit?.name ?? data[field.name] ?? t('NO_COST_CENTER')
      }
      default:
        return data?.[field.name]?.split(';base64')[0].split('|')[0] ?? ''
    }
  }

  const name = form.data?.tr?.[language] ?? form.data?.name ?? ''
  const parts = name.split('|')
  const title = parts[1] ?? parts[0] ?? ''

  return (
    <>
      <CsvModal
        sections={sections ?? []}
        columnCount={4}
        loading={loading}
        onClose={props.onClose}
        title={title}
        className="I4U"
        onPrint={() => undefined}
        visible={!!props.requestId}
      />
    </>
  )
}
